import {
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  Pipe,
  PipeTransform,
  inject,
} from '@angular/core';
import {
  formatCurrency,
  getCurrencySymbol,
  getNumberOfCurrencyDigits,
} from '@angular/common';
import { Decimal } from 'decimal.js';

/**
 * Currency
 *
 * Formats a monetary value in a currency, like Angular's `currency` pipe,
 * and also accepts a decimal.js Decimal (the Currency base type).
 */
@Pipe({
  name: 'currency',
  standalone: true,
})
export class CurrencyPipe implements PipeTransform {
  locale: string = inject(LOCALE_ID);

  defaultCurrencyCode: string = inject(DEFAULT_CURRENCY_CODE);

  transform(
    value: Decimal | number | string | null | undefined,
    currencyCode?: string,
    display: 'code' | 'symbol' | 'symbol-narrow' | string | boolean = 'symbol',
    digitsInfo?: string,
    locale?: string,
  ): string | null {
    // An empty value shows nothing, as with Angular's pipe
    if (value == null || value === '') return null;

    // Text that isn't a number is a template error, as with Angular's pipe
    let amount: Decimal;
    try {
      // Read the value as an exact Decimal
      amount = new Decimal(value);
    } catch {
      // Report the value Angular's pipe would have rejected
      throw new Error(
        `InvalidPipeArgument: '${value}' for pipe 'CurrencyPipe'`,
      );
    }

    // Resolve the arguments the way Angular's currency pipe does
    const code = currencyCode || this.defaultCurrencyCode;
    const formatLocale = locale || this.locale;
    const shownAs =
      typeof display === 'boolean' ? (display ? 'symbol' : 'code') : display;

    // Choose what stands for the currency: its code unless display asks otherwise
    let currency = code;
    if (shownAs === 'symbol' || shownAs === 'symbol-narrow') {
      // Look up the currency's wide or narrow symbol in the locale
      currency = getCurrencySymbol(
        code,
        shownAs === 'symbol' ? 'wide' : 'narrow',
        formatLocale,
      );
    }
    // Any other display text is shown as given
    else if (shownAs !== 'code') {
      // Use the display text itself in place of the symbol
      currency = shownAs;
    }

    /**
     * Round with Decimal before formatting
     *
     * Angular formats numbers, so the value becomes one only once it is
     * rounded to the digits it will show; a computed value such as
     * 0.1 + 0.2 never shows float drift, and half a cent rounds away from zero.
     */
    const rounded = amount.toDecimalPlaces(
      this.maxFractionDigits(code, digitsInfo),
      Decimal.ROUND_HALF_UP,
    );
    return formatCurrency(
      rounded.toNumber(),
      formatLocale,
      currency,
      code,
      digitsInfo,
    );
  }

  maxFractionDigits(currencyCode: string, digitsInfo?: string): number {
    // Start from the currency's own digits
    const currencyDigits = getNumberOfCurrencyDigits(currencyCode);

    // Without a readable digitsInfo, the currency's digits stand
    const parts = digitsInfo?.match(/^(\d+)?\.((\d+)(-(\d+))?)?$/);
    if (!parts) return currencyDigits;

    // An explicit maximum wins
    if (parts[5] != null) return parseInt(parts[5], 10);

    // A minimum above the currency's digits raises the maximum with it
    if (parts[3] != null)
      return Math.max(parseInt(parts[3], 10), currencyDigits);

    // Otherwise the currency's digits stand
    return currencyDigits;
  }
}
